import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { statsApi, formatCurrency } from '../services/api';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import BonusListModal from './BonusListModal';
import TravelCosts from './TravelCosts';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const StatsContainer = styled.div`
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .stats-header {
    display: flex;
    align-items: center;
    margin-bottom: 30px;
    gap: 20px;
    
    h2 {
      color: #333;
      font-size: 1.8rem;
      margin: 0;
    }
  }
  
  .year-selector {
    select {
      padding: 8px 12px;
      border-radius: 4px;
      border: 1px solid #ddd;
      font-size: 1rem;
    }
  }
  
  .tabs {
    display: flex;
    border-bottom: 1px solid #ddd;
    margin-bottom: 30px;
    
    .tab {
      padding: 12px 20px;
      cursor: pointer;
      border-bottom: 3px solid transparent;
      
      &.active {
        border-bottom: 3px solid #007bff;
        color: #007bff;
        font-weight: 600;
      }
      
      &:hover {
        background-color: #f8f9fa;
      }
    }
  }
  
  .tab-content {
      .summary {
        background-color: white;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        margin-bottom: 30px;
        position: relative; /* Add this line */
      
      .summary-text {
        font-size: 1.1rem;
        line-height: 1.6;
      }
    }
    
    .chart-container {
      background-color: white;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      margin-bottom: 30px;
      min-height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .chart-placeholder {
        text-align: center;
        color: #666;
        
        p {
          margin: 10px 0;
        }
      }
    }
  }
  
  @media (max-width: 768px) {
    .stats-header {
      flex-direction: column;
      align-items: flex-start;
      
      h2 {
        font-size: 1.5rem;
      }
    }
    
    .tabs {
      overflow-x: auto;
      
      .tab {
        white-space: nowrap;
      }
    }
  }
`;

const StatCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  margin-bottom: 20px;
  
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #007bff;
    margin: 10px 0;
  }
  
  .stat-label {
    font-size: 1.1rem;
    color: #666;
  }
`;

/*
 * 계좌 내역에 남기지 않는 저축 (연도별).
 *
 * 비트코인처럼 실제로 쓴 돈이 아닌데 수입·지출 거래로는 적고 싶지 않은 돈이다.
 *
 * 순저축액·저축률 자체는 손대지 않는다. 그 숫자는 계좌에 실제로 찍힌 거래의
 * 결과라서, 여기에 슬쩍 더해 버리면 "그래서 통장은 얼마냐"를 알 수 없게 된다.
 * 대신 통계 > 월급 탭 요약줄의 순저축액 옆에 회색 글씨로 "이걸 합치면 얼마"를
 * 덧붙인다. 월별 꺾은선도 그대로 둔다.
 *
 * 연도는 selectedYear 와 같은 문자열로 적는다.
 */
const OFF_BOOK_SAVINGS = {
  '2026': { amount: 30000000, label: '비트코인 3,000만 포함 시' },
};

function Stats() {
  const [activeTab, setActiveTab] = useState('portfolio');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [assetAllocation, setAssetAllocation] = useState({});
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalCash, setTotalCash] = useState(0);
  const [salaries, setSalaries] = useState([]);
  const [minGuiMedianSalary, setMinGuiMedianSalary] = useState(0); // Add state for MinGui median
  const [haYoungMedianSalary, setHaYoungMedianSalary] = useState(0); // Add state for HaYoung median
  const [monthlySalaryTotals, setMonthlySalaryTotals] = useState({}); // Add state for monthly totals
  const [hoveredBar, setHoveredBar] = useState(null);
  const [editingSalary, setEditingSalary] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [bonusListContext, setBonusListContext] = useState(null); // { month, person }
  const [showBonusListModal, setShowBonusListModal] = useState(false);
  const [bonusListEntries, setBonusListEntries] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [growthData, setGrowthData] = useState(null);
  const [baselineSnapshotId, setBaselineSnapshotId] = useState('');
  const [newSnapshotLabel, setNewSnapshotLabel] = useState('');
  const [growthError, setGrowthError] = useState(null);
  const [stockPortfolio, setStockPortfolio] = useState({ 민규: [], 하영: [] });
  const [stockError, setStockError] = useState(null);
  const [showStockAdd, setShowStockAdd] = useState({ 민규: false, 하영: false });
  const [editingStockEntry, setEditingStockEntry] = useState(null);
  const [stockNotes, setStockNotes] = useState({ 민규: '', 하영: '' });
  const [editingStockNote, setEditingStockNote] = useState(null);
  const [dragOverStockEntryId, setDragOverStockEntryId] = useState(null);
  const [stockOcrPerson, setStockOcrPerson] = useState('민규');
  const [stockOcrFiles, setStockOcrFiles] = useState([]);
  const [stockOcrPreview, setStockOcrPreview] = useState(null);
  const [stockOcrLoading, setStockOcrLoading] = useState(false);
  const [stockOcrApplying, setStockOcrApplying] = useState(false);
  const [yearEndTax, setYearEndTax] = useState(null);
  const [yearEndTaxError, setYearEndTaxError] = useState(null);
  const [yearEndTaxFiles, setYearEndTaxFiles] = useState([]);
  const [yearEndTaxImporting, setYearEndTaxImporting] = useState(false);
  const [yearEndTaxUploadPerson, setYearEndTaxUploadPerson] = useState('민규');
  const [yearEndTaxGrossIncomeForm, setYearEndTaxGrossIncomeForm] = useState({ 민규: '', 하영: '' });
  const [yearEndTaxForm, setYearEndTaxForm] = useState({
    person: '민규',
    category: '신용카드',
    amount: '',
    note: '',
  });
  const stockDragRef = useRef(null);

  // 월급 탭 위에 겹쳐 그리는 순저축 꺾은선 - 막대 위치는 flexbox가 정하므로
  // 실제 DOM에서 각 월 막대그룹의 중심 x좌표를 측정해 정확히 맞춘다
  const salaryBarRefs = useRef({});
  const salaryChartAreaRef = useRef(null);
  const [salaryLineGeometry, setSalaryLineGeometry] = useState({ points: [], areaHeight: 0 });
  const [hoveredSavingsMonth, setHoveredSavingsMonth] = useState(null);

  const [stockForm, setStockForm] = useState({
    민규: { category: '지수', market: '미국', listingMarket: '국장', name: '', purchaseAmount: '', currentAmount: '' },
    하영: { category: '지수', market: '미국', listingMarket: '국장', name: '', purchaseAmount: '', currentAmount: '' },
  });

  const GROWTH_CATEGORY_ORDER = ['현금', '부동산', '코인', '주식', '연금', '기타'];
  const YEAR_END_TAX_CATEGORIES = [
    '현금영수증',
    '신용카드',
    '직불카드',
    '전통시장',
    '대중교통',
    '의료비',
    '교육비',
    '보험료',
    '기부금',
    '연금저축',
    '월세',
    '기타',
  ];
  const STOCK_CATEGORY_COLORS = {
    '지수': '#1976D2',      // blue
    '섹터': '#2E7D32',      // green
    '섹터 ETF': '#2E7D32',  // legacy
    '종목': '#F57C00',      // orange
    '현금': '#607D8B',      // blue grey
  };

  const rgbaFromHex = (hex, alpha) => {
    const h = (hex || '').replace('#', '');
    if (h.length !== 6) return hex;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const formatReturnPct = (p) => {
    if (p === null || p === undefined) return '—';
    const sign = p >= 0 ? '+' : '';
    return `${sign}${p.toFixed(2)}%`;
  };

  const returnPctColor = (p) => {
    if (p === null || p === undefined || p === 0) return '#555';
    return p > 0 ? '#d32f2f' : '#1565c0';
  };

  const stockMarketLabel = (market) => {
    if (market === '미국') return '미장';
    if (market === '한국') return '국장';
    if (market === '글로벌') return '기타';
    return market || '국장';
  };

  const normalizeStockCategory = (category) => (
    category === '섹터 ETF' ? '섹터' : (category || '종목')
  );

  const formatSnapshotWhen = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const stockCurrentAmount = (entry) => Number(entry?.current_amount || entry?.amount || entry?.weight || 0);
  const stockPurchaseAmount = (entry) => Number(entry?.purchase_amount || entry?.amount || entry?.weight || 0);
  const stockReturnPct = (entry) => {
    const purchase = stockPurchaseAmount(entry);
    if (!purchase) return null;
    return ((stockCurrentAmount(entry) - purchase) / purchase) * 100;
  };

  // Debugging: Log hoveredBar state changes
  useEffect(() => {
    console.log('hoveredBar state changed:', hoveredBar);
  }, [hoveredBar]);

  useEffect(() => {
    fetchStats();
  }, []);

  const [monthlyIncome, setMonthlyIncome] = useState({});

  useLayoutEffect(() => {
    if (activeTab !== 'salary') return;
    const areaEl = salaryChartAreaRef.current;
    if (!areaEl) return;

    const computeGeometry = () => {
      const areaRect = areaEl.getBoundingClientRect();
      const points = Object.entries(salaryBarRefs.current)
        .map(([month, el]) => {
          if (!el) return null;
          const rect = el.getBoundingClientRect();
          return { month, x: rect.left + rect.width / 2 - areaRect.left };
        })
        .filter(Boolean);
      setSalaryLineGeometry({ points, areaHeight: areaRect.height });
    };

    computeGeometry();
    window.addEventListener('resize', computeGeometry);
    return () => window.removeEventListener('resize', computeGeometry);
  }, [activeTab, salaries, selectedYear]);

  const fetchStats = async () => {
    try {
      const [allocationResponse, assetsResponse, cashResponse, incomeResponse, salaryResponse, minGuiMedianResponse, haYoungMedianResponse, monthlyTotalsResponse] = await Promise.all([
        statsApi.getAssetAllocation(),
        statsApi.getTotalAssets(),
        statsApi.getTotalCash(),
        statsApi.getMonthlyIncomeBreakdown(selectedYear),
        statsApi.getSalaries(selectedYear),
        statsApi.getSalaryMedian(selectedYear, '민규'), // Fetch MinGui median
        statsApi.getSalaryMedian(selectedYear, '하영'), // Fetch HaYoung median
        statsApi.getMonthlySalaryTotals(selectedYear) // Fetch monthly totals
      ]);

      setAssetAllocation(allocationResponse.data);
      setTotalAssets(assetsResponse.data.total_assets);
      setTotalCash(cashResponse.data.total_cash);
      setMonthlyIncome(incomeResponse.data);
      setSalaries(salaryResponse.data);
      setMinGuiMedianSalary(minGuiMedianResponse.data.median_salary || 0); // Set MinGui median
      setHaYoungMedianSalary(haYoungMedianResponse.data.median_salary || 0); // Set HaYoung median
      setMonthlySalaryTotals(monthlyTotalsResponse.data.monthly_totals || {}); // Set monthly totals

      // In a full implementation, we would also set the other data
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [selectedYear]);

  useEffect(() => {
    if (activeTab !== 'growth') return undefined;
    let cancelled = false;
    (async () => {
      try {
        setGrowthError(null);
        const snapRes = await statsApi.getAssetSnapshots();
        if (cancelled) return;
        setSnapshots(snapRes.data || []);
        try {
          const growthRes = await statsApi.getAssetGrowth(baselineSnapshotId || undefined);
          if (!cancelled) {
            setGrowthData(growthRes.data);
          }
        } catch (ge) {
          if (!cancelled) {
            setGrowthData(null);
            setGrowthError(ge.response?.data?.detail || '증감 데이터를 불러오지 못했습니다.');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setSnapshots([]);
          setGrowthData(null);
          setGrowthError(e.response?.data?.detail || '데이터를 불러오지 못했습니다.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, baselineSnapshotId]);

  useEffect(() => {
    if (activeTab !== 'stock') return undefined;
    let cancelled = false;
    (async () => {
      try {
        setStockError(null);
        const [res, notesRes] = await Promise.all([
          statsApi.getStockPortfolio(),
          statsApi.getStockPortfolioNotes(),
        ]);
        if (!cancelled) {
          setStockPortfolio(res.data || { 민규: [], 하영: [] });
          setStockNotes(notesRes.data || { 민규: '', 하영: '' });
        }
      } catch (e) {
        if (!cancelled) {
          setStockPortfolio({ 민규: [], 하영: [] });
          setStockNotes({ 민규: '', 하영: '' });
          setStockError(e.response?.data?.detail || '주식 포폴 데이터를 불러오지 못했습니다.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'yearEndTax') return undefined;
    fetchYearEndTax();
    return undefined;
  }, [activeTab, selectedYear]);

  const handleAddStockEntry = async (person) => {
    try {
      const f = stockForm[person];
      const purchaseNum = typeof f.purchaseAmount === 'string' ? parseFloat(f.purchaseAmount) : f.purchaseAmount;
      const currentNum = typeof f.currentAmount === 'string' ? parseFloat(f.currentAmount) : f.currentAmount;
      await statsApi.addStockEntry(person, f.category, f.market, f.listingMarket, f.name, purchaseNum, currentNum || purchaseNum);
      setStockForm((prev) => ({ ...prev, [person]: { ...prev[person], name: '', purchaseAmount: '', currentAmount: '' } }));
      const res = await statsApi.getStockPortfolio();
      setStockPortfolio(res.data || { 민규: [], 하영: [] });
      setStockError(null);
    } catch (e) {
      alert(e.response?.data?.detail || '추가에 실패했습니다.');
    }
  };

  const handleDeleteStockEntry = async (person, entryId) => {
    if (!window.confirm('이 항목을 삭제할까요?')) return;
    try {
      await statsApi.deleteStockEntry(person, entryId);
      const res = await statsApi.getStockPortfolio();
      setStockPortfolio(res.data || { 민규: [], 하영: [] });
      setEditingStockEntry(null);
      setStockError(null);
    } catch (e) {
      alert(e.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  const handleUpdateStockEntry = async () => {
    if (!editingStockEntry) return;
    try {
      const purchaseNum = typeof editingStockEntry.purchase_amount === 'string'
        ? parseFloat(editingStockEntry.purchase_amount)
        : editingStockEntry.purchase_amount;
      const currentNum = typeof editingStockEntry.current_amount === 'string'
        ? parseFloat(editingStockEntry.current_amount)
        : editingStockEntry.current_amount;
      if (!editingStockEntry.name.trim() || !Number.isFinite(purchaseNum) || purchaseNum <= 0 || !Number.isFinite(currentNum) || currentNum <= 0) {
        alert('종목명, 매입금액, 현재 평가금액을 입력하세요.');
        return;
      }
      await statsApi.updateStockEntry(
        editingStockEntry.person,
        editingStockEntry.id,
        editingStockEntry.category,
        editingStockEntry.market,
        editingStockEntry.listing_market,
        editingStockEntry.name,
        purchaseNum,
        currentNum
      );
      const res = await statsApi.getStockPortfolio();
      setStockPortfolio(res.data || { 민규: [], 하영: [] });
      setEditingStockEntry(null);
      setStockError(null);
    } catch (e) {
      alert(e.response?.data?.detail || '수정에 실패했습니다.');
    }
  };

  const fetchYearEndTax = async () => {
    try {
      const res = await statsApi.getYearEndTax(selectedYear);
      setYearEndTax(res.data);
      setYearEndTaxGrossIncomeForm({
        민규: res.data?.gross_income_by_person?.민규 ? String(res.data.gross_income_by_person.민규) : '',
        하영: res.data?.gross_income_by_person?.하영 ? String(res.data.gross_income_by_person.하영) : '',
      });
      setYearEndTaxError(null);
    } catch (e) {
      setYearEndTaxError(e.response?.data?.detail || '연말정산 데이터를 불러오지 못했습니다.');
    }
  };

  const handleSaveYearEndTaxGrossIncome = async (person) => {
    const grossIncome = Number(yearEndTaxGrossIncomeForm[person]);
    if (!Number.isFinite(grossIncome) || grossIncome < 0) {
      alert('세전 총급여를 0 이상으로 입력하세요.');
      return;
    }
    try {
      await statsApi.updateYearEndTaxGrossIncome(Number(selectedYear), person, grossIncome);
      await fetchYearEndTax();
      setYearEndTaxError(null);
    } catch (e) {
      setYearEndTaxError(e.response?.data?.detail || '세전 총급여 저장에 실패했습니다.');
    }
  };

  const handleImportCashReceipts = async () => {
    if (!yearEndTaxFiles.length) {
      alert('홈택스 현금영수증 엑셀 파일을 선택하세요.');
      return;
    }
    setYearEndTaxImporting(true);
    try {
      const res = await statsApi.importCashReceipts(selectedYear, yearEndTaxUploadPerson, yearEndTaxFiles);
      setYearEndTaxFiles([]);
      await fetchYearEndTax();
      setYearEndTaxError(null);
      alert(`현금영수증 ${formatCurrency(res.data.total_amount)}이 등록되었습니다.`);
    } catch (e) {
      setYearEndTaxError(e.response?.data?.detail || '현금영수증 엑셀 업로드에 실패했습니다.');
    } finally {
      setYearEndTaxImporting(false);
    }
  };

  const handleAddYearEndTaxEntry = async () => {
    const amount = Number(yearEndTaxForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('0보다 큰 금액을 입력하세요.');
      return;
    }
    try {
      await statsApi.addYearEndTaxEntry({
        year: Number(selectedYear),
        person: yearEndTaxForm.person,
        category: yearEndTaxForm.category,
        amount,
        source: 'manual',
        note: yearEndTaxForm.note,
      });
      setYearEndTaxForm((prev) => ({ ...prev, amount: '', note: '' }));
      await fetchYearEndTax();
      setYearEndTaxError(null);
    } catch (e) {
      setYearEndTaxError(e.response?.data?.detail || '연말정산 항목 추가에 실패했습니다.');
    }
  };

  const handleDeleteYearEndTaxEntry = async (entryId) => {
    if (!window.confirm('이 연말정산 항목을 삭제할까요?')) return;
    try {
      await statsApi.deleteYearEndTaxEntry(entryId);
      await fetchYearEndTax();
      setYearEndTaxError(null);
    } catch (e) {
      setYearEndTaxError(e.response?.data?.detail || '연말정산 항목 삭제에 실패했습니다.');
    }
  };

  const handlePreviewStockOcrAiImages = async () => {
    if (!stockOcrFiles.length) {
      alert('스크린샷 이미지를 선택하세요.');
      return;
    }
    setStockOcrLoading(true);
    try {
      const res = await statsApi.previewStockOcrAiImages(stockOcrPerson, stockOcrFiles);
      setStockOcrPreview(res.data);
      setStockError(null);
    } catch (e) {
      setStockOcrPreview(null);
      setStockError(e.response?.data?.detail || 'AI 이미지 분석에 실패했습니다.');
    } finally {
      setStockOcrLoading(false);
    }
  };

  const handleApplyStockOcr = async () => {
    const matches = stockOcrPreview?.matches || [];
    const unmatchedHoldings = stockOcrPreview?.unmatched_holdings
      || (matches.length ? [] : (stockOcrPreview?.extracted_holdings || []));
    const updates = [
      ...matches.map((match) => ({
        entry_id: match.entry_id,
        name: match.name,
        recognized_name: match.recognized_name,
        current_amount: match.recognized_current_amount,
        purchase_amount: match.recognized_purchase_amount,
      })),
      ...unmatchedHoldings.map((holding) => ({
        name: holding.name,
        current_amount: holding.current_amount,
        purchase_amount:
          holding.average_purchase_price != null && holding.quantity != null
            ? Number(holding.average_purchase_price) * Number(holding.quantity)
            : null,
      })),
    ].filter((update) => update.name && Number(update.current_amount) > 0);

    if (!updates.length) {
      alert('적용할 인식 결과가 없습니다.');
      return;
    }
    if (!window.confirm(`AI 인식 결과 ${updates.length}개를 아래 포트폴리오에 적용할까요?`)) return;
    setStockOcrApplying(true);
    try {
      await statsApi.applyStockOcrUpdates(stockOcrPreview.person, updates);
      const res = await statsApi.getStockPortfolio();
      setStockPortfolio(res.data || { 민규: [], 하영: [] });
      setStockOcrPreview(null);
      setStockError(null);
    } catch (e) {
      setStockError(e.response?.data?.detail || 'OCR 결과 반영에 실패했습니다.');
    } finally {
      setStockOcrApplying(false);
    }
  };

  const handleStockDragStart = (event, person, entryId) => {
    stockDragRef.current = { person, entryId };
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', entryId);
  };

  const handleStockDragOver = (event, person, entryId) => {
    const source = stockDragRef.current;
    if (!source || source.person !== person || source.entryId === entryId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverStockEntryId(entryId);
  };

  const handleStockDrop = async (event, person, targetEntryId) => {
    event.preventDefault();
    const source = stockDragRef.current;
    setDragOverStockEntryId(null);
    if (!source || source.person !== person || source.entryId === targetEntryId) return;

    const entries = stockPortfolio?.[person] || [];
    const fromIndex = entries.findIndex((entry) => entry.id === source.entryId);
    const toIndex = entries.findIndex((entry) => entry.id === targetEntryId);
    if (fromIndex < 0 || toIndex < 0) return;

    const reordered = [...entries];
    const [movedEntry] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, movedEntry);
    setStockPortfolio((prev) => ({ ...prev, [person]: reordered }));

    try {
      await statsApi.reorderStockEntries(person, reordered.map((entry) => entry.id));
      setStockError(null);
    } catch (e) {
      try {
        const res = await statsApi.getStockPortfolio();
        setStockPortfolio(res.data || { 민규: [], 하영: [] });
      } catch {
        setStockPortfolio((prev) => ({ ...prev, [person]: entries }));
      }
      alert(e.response?.data?.detail || '순서 변경에 실패했습니다.');
    }
  };

  const handleStockDragEnd = () => {
    setDragOverStockEntryId(null);
    setTimeout(() => {
      stockDragRef.current = null;
    }, 0);
  };

  const handleSaveStockNote = async () => {
    if (!editingStockNote) return;
    try {
      await statsApi.updateStockPortfolioNote(editingStockNote.person, editingStockNote.note);
      setStockNotes((prev) => ({ ...prev, [editingStockNote.person]: editingStockNote.note }));
      setEditingStockNote(null);
      setStockError(null);
    } catch (e) {
      alert(e.response?.data?.detail || '메모 저장에 실패했습니다.');
    }
  };

  const handleCreateAssetSnapshot = async () => {
    try {
      await statsApi.createAssetSnapshot(newSnapshotLabel.trim());
      setNewSnapshotLabel('');
      const snapRes = await statsApi.getAssetSnapshots();
      setSnapshots(snapRes.data || []);
      const growthRes = await statsApi.getAssetGrowth(baselineSnapshotId || undefined);
      setGrowthData(growthRes.data);
      setGrowthError(null);
      alert('저장 지점이 추가되었습니다.');
    } catch (e) {
      alert(e.response?.data?.detail || '저장에 실패했습니다.');
    }
  };

  const handleDeleteSnapshot = async (id) => {
    if (!window.confirm('이 저장 지점을 삭제할까요?')) return;
    try {
      await statsApi.deleteAssetSnapshot(id);
      const nextBaseline = baselineSnapshotId === id ? '' : baselineSnapshotId;
      setBaselineSnapshotId(nextBaseline);
      const snapRes = await statsApi.getAssetSnapshots();
      setSnapshots(snapRes.data || []);
      try {
        const growthRes = await statsApi.getAssetGrowth(nextBaseline || undefined);
        setGrowthData(growthRes.data);
        setGrowthError(null);
      } catch (ge) {
        setGrowthData(null);
        setGrowthError(ge.response?.data?.detail || '증감을 다시 불러오지 못했습니다.');
      }
    } catch (e) {
      alert(e.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  const renderPortfolioTab = () => {
    const legendMargin = {
      id: 'legendMargin',
      beforeInit(chart) {
        if (chart.legend) {
          const originalFit = chart.legend.fit;
          chart.legend.fit = function fit() {
            if (originalFit) {
              originalFit.bind(chart.legend)();
            }
            this.width += 100; // Increase width reserve
          };
        }
      },
      afterUpdate(chart) {
        // Shift the legend text to the right to create visual gap
        if (chart.legend) {
          chart.legend.left += 80;
        }
      }
    };

    const getColorForCategory = (category) => {
      const colors = {
        "현금": "#4CAF50",
        "부동산": "#2196F3",
        "코인": "#FF9800",
        "연금": "#FF5722", // Distinct color for pension
        "주식": "#9C27B0",
        "기타": "#607D8B"
      };
      return colors[category] || "#9E9E9E";
    };

    const calculatePercentage = (value, total) => {
      return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
    };

    const total = Object.values(assetAllocation).reduce((sum, value) => sum + value, 0);

    const chartData = {
      labels: Object.keys(assetAllocation),
      datasets: [
        {
          data: Object.values(assetAllocation),
          backgroundColor: Object.keys(assetAllocation).map(category => getColorForCategory(category)),
          borderColor: Object.keys(assetAllocation).map(category => getColorForCategory(category)),
          borderWidth: 1,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            font: {
              size: 14,
            },
            generateLabels: (chart) => {
              const data = chart.data;
              const total = Object.values(assetAllocation).reduce((sum, value) => sum + value, 0);
              if (data.labels.length && data.datasets.length) {
                return data.labels.map((label, i) => {
                  const style = data.datasets[0].backgroundColor[i];
                  const value = data.datasets[0].data[i];
                  const percentage = calculatePercentage(value, total);
                  return {
                    text: label,
                    fillStyle: style,
                    strokeStyle: style,
                    lineWidth: 1,
                    hidden: isNaN(value) || chart.getDatasetMeta(0).data[i].hidden,
                    index: i,
                  };
                });
              }
              return [];
            },
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const label = context.label || '';
              const value = context.parsed;
              const percentage = calculatePercentage(value, total);
              return `${label}: ${formatCurrency(value)} (${percentage}%)`;
            }
          }
        },
        datalabels: {
          color: '#fff',
          formatter: (value, context) => {
            const percentage = calculatePercentage(value, total);
            return percentage > 0 ? `${percentage}%` : '';
          },
          font: {
            weight: 'bold',
            size: 14,
          },
          display: function (context) {
            // Only display datalabels if the segment is large enough to show the label clearly
            // You might need to adjust this threshold based on your chart size and data distribution
            return context.dataset.data[context.dataIndex] / total * 100 > 5;
          }
        }
      },
    };

    return (
      <div className="tab-content">
        <div className="summary">
          <div className="summary-text">
            {Object.keys(assetAllocation).length > 0 ? (
              <>
                {Object.entries(assetAllocation).map(([category, value]) => (
                  <div key={category}>
                    {category}: {formatCurrency(value)} ({calculatePercentage(value, total)}%)
                  </div>
                ))}
              </>
            ) : (
              "자산 데이터가 없습니다."
            )}
          </div>
        </div>

        <div className="chart-container">
          {Object.keys(assetAllocation).length > 0 ? (
            <div style={{ width: '100%', maxWidth: '500px', height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Doughnut data={chartData} options={chartOptions} plugins={[legendMargin]} />
            </div>
          ) : (
            <div className="chart-placeholder">
              <p>자산 데이터가 없습니다.</p>
              <p>계좌를 추가하고 거래를 등록해보세요.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGrowthTab = () => {
    const baseline = growthData?.baseline_snapshot;
    const byCat = growthData?.by_category || {};
    const totalRow = growthData?.total;

    return (
      <div className="tab-content">
        <div className="summary">
          <div className="summary-text">
            <p style={{ fontSize: '1.7rem', fontWeight: 'bold', marginBottom: '12px' }}>자라나라 자산자산</p>
            <p style={{ fontSize: '0.95rem', color: '#555', marginBottom: '16px' }}>
            「지금 자산 저장」으로 현재 포트폴리오를 저장 지점으로 남기면, 이후 같은 방식으로 쌓인 시점과 비교해
              카테고리별·총 수익률을 볼 수 있습니다.
              <br />
              비교 기준은 기본적으로 <strong>올해 가장 먼저 저장한 지점</strong>이라, 새로 저장해도 기준은
              그대로입니다. 올해 저장한 게 아직 없으면 그 이전의 마지막 저장 지점을 씁니다.
              아래에서 다른 저장 지점을 직접 고를 수도 있습니다.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label htmlFor="baseline-snapshot">비교 기준 시점</label>
                <select
                  id="baseline-snapshot"
                  value={baselineSnapshotId}
                  onChange={(e) => setBaselineSnapshotId(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', minWidth: '260px' }}
                >
                  <option value="">올해 첫 저장 지점 (기본)</option>
                  {snapshots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {formatSnapshotWhen(s.created_at)}
                      {s.label ? ` — ${s.label}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 200px' }}>
                <label htmlFor="snapshot-label">메모 (선택)</label>
                <input
                  id="snapshot-label"
                  type="text"
                  value={newSnapshotLabel}
                  onChange={(e) => setNewSnapshotLabel(e.target.value)}
                  placeholder="예: 연말 결산"
                  style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <button
                type="button"
                onClick={handleCreateAssetSnapshot}
                style={{
                  padding: '10px 16px',
                  background: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                지금 자산 저장
              </button>
            </div>

            {growthError && (
              <p style={{ color: '#c62828', marginBottom: '12px' }}>{growthError}</p>
            )}

            {baseline && (
              <p style={{ marginBottom: '8px', color: '#333' }}>
                <strong>기준 시점</strong>: {formatSnapshotWhen(baseline.created_at)}
                {baseline.label ? ` (${baseline.label})` : ''}
                {' · '}
                <strong>기준 총액</strong> {formatCurrency(baseline.total)}
              </p>
            )}
          </div>
        </div>

        <div className="chart-container" style={{ minHeight: 'auto', display: 'block', padding: '16px' }}>
          {growthData && totalRow && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                    <th style={{ padding: '10px 8px' }}>구분</th>
                    <th style={{ padding: '10px 8px' }}>기준 금액</th>
                    <th style={{ padding: '10px 8px' }}>현재 금액</th>
                    <th style={{ padding: '10px 8px' }}>증감</th>
                    <th style={{ padding: '10px 8px' }}>수익률</th>
                  </tr>
                </thead>
                <tbody>
                  {GROWTH_CATEGORY_ORDER.map((key) => {
                    const row = byCat[key];
                    if (!row) return null;
                    return (
                      <tr key={key} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px 8px', fontWeight: 600 }}>{key}</td>
                        <td style={{ padding: '10px 8px' }}>{formatCurrency(row.baseline)}</td>
                        <td style={{ padding: '10px 8px' }}>{formatCurrency(row.current)}</td>
                        <td style={{ padding: '10px 8px', color: row.delta >= 0 ? '#2e7d32' : '#c62828' }}>
                          {row.delta >= 0 ? '+' : ''}
                          {formatCurrency(row.delta)}
                        </td>
                        <td style={{ padding: '10px 8px' }}>{formatReturnPct(row.return_pct)}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
                    <td style={{ padding: '12px 8px' }}>총합</td>
                    <td style={{ padding: '12px 8px' }}>{formatCurrency(totalRow.baseline)}</td>
                    <td style={{ padding: '12px 8px' }}>{formatCurrency(totalRow.current)}</td>
                    <td
                      style={{
                        padding: '12px 8px',
                        color: totalRow.delta >= 0 ? '#2e7d32' : '#c62828',
                      }}
                    >
                      {totalRow.delta >= 0 ? '+' : ''}
                      {formatCurrency(totalRow.delta)}
                    </td>
                    <td style={{ padding: '12px 8px' }}>{formatReturnPct(totalRow.return_pct)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {snapshots.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <p style={{ fontWeight: 600, marginBottom: '8px' }}>저장 지점 목록</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {snapshots.map((s) => (
                  <li
                    key={s.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: '1px solid #eee',
                      gap: '12px',
                    }}
                  >
                    <span>
                      {formatSnapshotWhen(s.created_at)}
                      {s.label ? ` — ${s.label}` : ''}
                      {' · '}
                      {formatCurrency(s.total)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeleteSnapshot(s.id)}
                      style={{
                        padding: '6px 10px',
                        fontSize: '0.85rem',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        background: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      삭제
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStockPortfolioTab = () => {
    const people = ['민규', '하영'];

    const buildChart = (entries) => {
      const cleaned = (entries || []).filter((e) => e && e.name && stockCurrentAmount(e) > 0);
      const total = cleaned.reduce((sum, e) => sum + stockCurrentAmount(e), 0) || 0;
      const grouped = cleaned.reduce((groups, e) => {
        const category = normalizeStockCategory(e.category);
        const market = category === '현금' ? (e.market || '원화') : e.market;
        const key = `${category}/${market}`;
        if (!groups[key]) {
          groups[key] = {
            category,
            market,
            amount: 0,
          };
        }
        groups[key].amount += stockCurrentAmount(e);
        return groups;
      }, {});
      const categories = Object.values(grouped);
      const labels = categories.map((group) => `${group.category}/${stockMarketLabel(group.market)}`);
      const data = categories.map((group) => group.amount);
      const backgroundColor = categories.map((group) => {
        const base = STOCK_CATEGORY_COLORS[group.category] || '#9E9E9E';
        const alpha = group.market === '미국' || group.market === '달러' ? 0.45 : 0.95;
        return rgbaFromHex(base, alpha);
      });
      const borderColor = categories.map((group) => {
        const base = STOCK_CATEGORY_COLORS[group.category] || '#9E9E9E';
        return rgbaFromHex(base, 1);
      });
      return {
        total,
        chartData: {
          labels,
          datasets: [
            {
              data,
              backgroundColor,
              borderColor,
              borderWidth: 1,
            },
          ],
        },
      };
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            boxWidth: 12,
            font: { size: 12 },
            generateLabels: (chart) => {
              const ds = chart.data.datasets[0] || {};
              return (chart.data.labels || []).map((label, i) => {
                return {
                  text: label,
                  fillStyle: ds.backgroundColor?.[i],
                  strokeStyle: ds.borderColor?.[i],
                  lineWidth: 1,
                  hidden: chart.getDatasetMeta(0).data[i]?.hidden,
                  index: i,
                };
              });
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const w = Number(ctx.parsed || 0);
              const total = (ctx.dataset.data || []).reduce((s, v) => s + Number(v || 0), 0) || 1;
              const pct = (w / total) * 100;
              return `${ctx.label}: ${formatCurrency(w)} (${pct.toFixed(1)}%)`;
            },
          },
        },
        datalabels: {
          color: '#111',
          formatter: (value, ctx) => {
            const total = (ctx.chart.data.datasets[0].data || []).reduce((s, v) => s + Number(v || 0), 0) || 0;
            if (!total) return '';
            const pct = (Number(value) / total) * 100;
            return pct >= 6 ? `${pct.toFixed(0)}%` : ''; // hide tiny labels
          },
        },
      },
    };

    const renderPerson = (person) => {
      const entries = stockPortfolio?.[person] || [];
      const { total, chartData } = buildChart(entries);
      return (
        <div style={{ flex: 1, minWidth: '320px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
            <p style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0 }}>{person}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                aria-label={`${person} 메모 보기`}
                title={stockNotes[person] ? '메모 보기/수정' : '메모 추가'}
                onClick={() => setEditingStockNote({ person, note: stockNotes[person] || '' })}
                style={{
                  width: '20px',
                  height: '20px',
                  padding: 0,
                  border: `1px solid ${stockNotes[person] ? '#007bff' : '#888'}`,
                  borderRadius: '50%',
                  background: stockNotes[person] ? '#eef6ff' : '#fff',
                  color: stockNotes[person] ? '#007bff' : '#666',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                ?
              </button>
              <span style={{ color: '#666', fontSize: '0.95rem' }}>현재 평가총액: {formatCurrency(total)}</span>
            </div>
          </div>

          <div style={{ height: '320px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', padding: '12px', marginBottom: '14px' }}>
            {entries.length ? (
              <Doughnut data={chartData} options={chartOptions} />
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                아직 입력된 종목이 없습니다.
              </div>
            )}
          </div>

          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', padding: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <p style={{ fontWeight: 700, marginTop: 0, marginBottom: 0 }}>보유 종목</p>
              <button
                type="button"
                onClick={() => setShowStockAdd((prev) => ({ ...prev, [person]: !prev[person] }))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
              >
                {showStockAdd[person] ? '닫기' : '추가하기'}
              </button>
            </div>

            {showStockAdd[person] && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: stockForm[person].category === '현금' ? '1fr 1fr' : '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.9rem', color: '#444' }}>카테고리</label>
                <select
                  value={stockForm[person].category}
                  onChange={(e) =>
                    setStockForm((prev) => ({
                      ...prev,
                      [person]: {
                        ...prev[person],
                        category: e.target.value,
                        market: e.target.value === '현금' ? '원화' : '미국',
                        listingMarket: e.target.value === '현금' ? '' : '국장',
                      },
                    }))
                  }
                  style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="지수">지수</option>
                  <option value="섹터">섹터</option>
                  <option value="종목">종목</option>
                  <option value="현금">현금</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.9rem', color: '#444' }}>
                  {stockForm[person].category === '현금' ? '통화' : '시장'}
                </label>
                <select
                  value={stockForm[person].market}
                  onChange={(e) =>
                    setStockForm((prev) => ({
                      ...prev,
                      [person]: { ...prev[person], market: e.target.value },
                    }))
                  }
                  style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  {stockForm[person].category === '현금' ? (
                    <>
                      <option value="원화">원화</option>
                      <option value="달러">달러</option>
                    </>
                  ) : (
                    <>
                      <option value="한국">국장</option>
                      <option value="미국">미장</option>
                      <option value="기타">기타</option>
                    </>
                  )}
                </select>
              </div>
              {stockForm[person].category !== '현금' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.9rem', color: '#444' }}>상장시장</label>
                  <select
                    value={stockForm[person].listingMarket}
                    onChange={(e) =>
                      setStockForm((prev) => ({
                        ...prev,
                        [person]: { ...prev[person], listingMarket: e.target.value },
                      }))
                    }
                    style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="국장">국장</option>
                    <option value="미장">미장</option>
                  </select>
                </div>
              )}
                </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.9rem', color: '#444' }}>종목명</label>
                <input
                  value={stockForm[person].name}
                  onChange={(e) =>
                    setStockForm((prev) => ({
                      ...prev,
                      [person]: { ...prev[person], name: e.target.value },
                    }))
                  }
                  placeholder="예: VOO / QQQ / 삼성전자"
                  style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.9rem', color: '#444' }}>매입금액</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={stockForm[person].purchaseAmount}
                  onChange={(e) =>
                    setStockForm((prev) => ({
                      ...prev,
                      [person]: { ...prev[person], purchaseAmount: e.target.value },
                    }))
                  }
                  placeholder="예: 1000000"
                  style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.9rem', color: '#444' }}>현재 평가금액</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={stockForm[person].currentAmount}
                  onChange={(e) =>
                    setStockForm((prev) => ({
                      ...prev,
                      [person]: { ...prev[person], currentAmount: e.target.value },
                    }))
                  }
                  placeholder="미입력 시 매입금액"
                  style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => handleAddStockEntry(person)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                borderRadius: '6px',
                background: '#111',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              추가
            </button>
              </div>
            )}

            {entries.length > 0 && (
              <div style={{ marginTop: '14px' }}>
                <p style={{ fontWeight: 700, marginBottom: '8px' }}>
                  현재 목록 <span style={{ color: '#777', fontSize: '0.85rem', fontWeight: 400 }}>드래그로 순서 변경, 눌러 수정/삭제</span>
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {entries.map((e) => (
                    <li
                      key={e.id}
                      draggable
                      onDragStart={(event) => handleStockDragStart(event, person, e.id)}
                      onDragOver={(event) => handleStockDragOver(event, person, e.id)}
                      onDrop={(event) => handleStockDrop(event, person, e.id)}
                      onDragEnd={handleStockDragEnd}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (stockDragRef.current) return;
                          setEditingStockEntry({
                            ...e,
                            person,
                            category: normalizeStockCategory(e.category),
                            market: e.market === '글로벌' ? '기타' : e.market,
                            purchase_amount: stockPurchaseAmount(e) || '',
                            current_amount: stockCurrentAmount(e) || '',
                            listing_market: e.listing_market || (e.category === '현금' ? '' : '국장'),
                          });
                        }}
                        style={{
                          display: 'flex',
                          width: '100%',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 8px',
                          fontSize: '0.95rem',
                          border: 'none',
                          borderBottom: '1px solid #eee',
                          background: dragOverStockEntryId === e.id ? '#eef6ff' : '#fff',
                          color: '#111',
                          cursor: 'grab',
                          textAlign: 'left',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                        }}
                      >
                        <span>
                          <strong>{e.name}</strong> — 현재 {formatCurrency(stockCurrentAmount(e))} / 매입 {formatCurrency(stockPurchaseAmount(e))}
                          {' '}
                          <span style={{ color: returnPctColor(stockReturnPct(e)), fontWeight: 700 }}>
                            ({formatReturnPct(stockReturnPct(e))})
                          </span>
                          {' '}
                          <span style={{ color: '#777' }}>({normalizeStockCategory(e.category)}/{e.category === '현금' ? (e.market || '원화') : stockMarketLabel(e.market)})</span>
                        </span>
                        <span style={{ color: '#111', fontSize: '0.8rem', fontWeight: 700, marginLeft: '10px', whiteSpace: 'nowrap' }}>
                          {total ? `${((stockCurrentAmount(e) / total) * 100).toFixed(1)}%` : '0.0%'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="tab-content">
        <div className="summary">
          <div className="summary-text">
            <p style={{ fontSize: '1.7rem', fontWeight: 'bold', marginBottom: '12px' }}>주식 포폴</p>
            <p style={{ fontSize: '0.95rem', color: '#555', marginBottom: '0' }}>
              차트 비중은 현재 평가금액 기준입니다. 매입금액은 수익률 계산에 쓰고, 스크린샷 OCR 결과는 미리보기 확인 후 현재 평가금액에 반영합니다.
            </p>
            {stockError && <p style={{ color: '#c62828', marginTop: '10px' }}>{stockError}</p>}
          </div>
        </div>

        <div className="chart-container" style={{ minHeight: 'auto', display: 'block', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', padding: '14px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <p style={{ fontWeight: 700, margin: 0 }}>스크린샷 OCR 업데이트</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label htmlFor="stock-ocr-person" style={{ fontSize: '0.9rem', color: '#555' }}>대상</label>
                <select
                  id="stock-ocr-person"
                  value={stockOcrPerson}
                  onChange={(e) => {
                    setStockOcrPerson(e.target.value);
                    setStockOcrPreview(null);
                  }}
                  style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="민규">민규</option>
                  <option value="하영">하영</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  setStockOcrFiles(Array.from(e.target.files || []));
                  setStockOcrPreview(null);
                }}
                style={{ width: '100%', marginBottom: '8px' }}
              />
              <button
                type="button"
                onClick={handlePreviewStockOcrAiImages}
                disabled={stockOcrLoading}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  border: '1px solid #111',
                  borderRadius: '6px',
                  background: '#111',
                  color: '#fff',
                  cursor: stockOcrLoading ? 'default' : 'pointer',
                  fontWeight: 700,
                }}
              >
                {stockOcrLoading ? 'AI가 이미지 분석 중...' : 'AI 이미지 분석 미리보기'}
              </button>
            </div>

            {stockOcrLoading && (
              <div style={{
                margin: '10px 0',
                padding: '12px',
                border: '1px solid #cfe2ff',
                borderRadius: '6px',
                background: '#f3f8ff',
                color: '#174ea6',
                fontWeight: 700,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    width: '16px',
                    height: '16px',
                    border: '3px solid #b6d4fe',
                    borderTopColor: '#174ea6',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  <span>AI가 스크린샷을 분석하고 있습니다. 잠시만 기다려주세요.</span>
                </div>
              </div>
            )}

            {stockOcrPreview && (
              <div style={{ marginTop: '12px', overflowX: 'auto' }}>
                <p style={{ margin: '0 0 8px', fontWeight: 700 }}>
                  AI 인식 결과
                  {stockOcrPreview.engine === 'openai' && stockOcrPreview.model ? ` · ${stockOcrPreview.model}` : ''}
                </p>
                <pre style={{
                  margin: '0 0 14px',
                  padding: '12px',
                  background: '#f6f8fa',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  color: '#222',
                  fontSize: '0.85rem',
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {JSON.stringify(stockOcrPreview.extracted_holdings || [], null, 2)}
                </pre>
                <button
                  type="button"
                  onClick={handleApplyStockOcr}
                  disabled={stockOcrApplying}
                  style={{
                    padding: '10px 14px',
                    border: 'none',
                    borderRadius: '6px',
                    background: stockOcrApplying ? '#90caf9' : '#007bff',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: stockOcrApplying ? 'default' : 'pointer',
                  }}
                >
                  {stockOcrApplying ? '적용 중...' : '인식 결과 적용'}
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {people.map((p) => renderPerson(p))}
          </div>
        </div>
      </div>
    );
  };

  const renderSavingsTab = () => {
    // Calculate summary statistics
    let totalSavings = 0;
    let totalInterest = 0;

    Object.values(monthlyIncome).forEach(monthData => {
      totalSavings += (monthData.savings || 0);
      totalInterest += (monthData.interest || 0);
    });

    const total = totalSavings + totalInterest;

    // Sort months chronologically for chart
    const sortedMonths = Object.keys(monthlyIncome).sort();

    // Max value for chart scaling (savings + interest per month)
    const maxTotal = Math.max(
      ...Object.values(monthlyIncome).map((d) => (d.savings || 0) + (d.interest || 0)),
      1
    );

    // Y-axis: round up to nearest 100,000, 5 grid lines
    const roundedMax = Math.ceil(maxTotal / 100000) * 100000;
    const gridInterval = roundedMax / 5;
    const yAxisValues = [
      roundedMax,
      roundedMax - gridInterval,
      roundedMax - 2 * gridInterval,
      roundedMax - 3 * gridInterval,
      roundedMax - 4 * gridInterval,
      0
    ];

    const maxBarHeight = 250;
    const SAVINGS_COLOR = '#4CAF50';   // green
    const INTEREST_COLOR = '#FFC107'; // yellow

    return (
      <div className="tab-content">
        <div className="summary">
          <div className="summary-text">
            <p style={{ fontSize: '1.7rem', fontWeight: 'bold', marginBottom: '10px' }}>{selectedYear}년 저축+이자 요약</p>
            {activeTab === 'savings' && (
              <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '1rem' }}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}년</option>
                  ))}
                </select>
              </div>
            )}
            <p>저축: {formatCurrency(totalSavings)}</p>
            <p>이자: {formatCurrency(totalInterest)}</p>
            <p>합계: {formatCurrency(total)}</p>
          </div>
        </div>

        <div className="chart-container">
          {sortedMonths.length > 0 ? (
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ textAlign: 'center', marginBottom: '10px', fontSize: '18px', fontWeight: 'bold' }}>
                저축+이자
              </div>

              <div style={{ display: 'flex', height: '300px', marginBottom: '10px' }}>
                <div style={{ width: '55px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: '10px', fontSize: '10px', color: '#666', lineHeight: '1', position: 'relative', zIndex: '1', boxSizing: 'border-box', height: 'calc(100% - 20px)' }}>
                  <span>{(yAxisValues[0] / 10000).toLocaleString()}만원</span>
                  <span>{(yAxisValues[1] / 10000).toLocaleString()}만원</span>
                  <span>{(yAxisValues[2] / 10000).toLocaleString()}만원</span>
                  <span>{(yAxisValues[3] / 10000).toLocaleString()}만원</span>
                  <span>{(yAxisValues[4] / 10000).toLocaleString()}만원</span>
                  <span>0-</span>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                  <div
                    style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '2%', paddingBottom: '20px', borderBottom: '1px solid #ddd', position: 'relative', paddingLeft: '0.5%', paddingRight: '0.5%' }}
                  >
                    <div style={{ position: 'absolute', top: '0px', left: 0, right: 0, bottom: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingTop: '0px', paddingBottom: '0px' }}>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} style={{ height: '1px', backgroundColor: '#eee', width: '100%', zIndex: '0' }}></div>
                      ))}
                    </div>
                    {sortedMonths.map((month) => {
                      const data = monthlyIncome[month];
                      const savings = data.savings || 0;
                      const interest = data.interest || 0;
                      const monthTotal = savings + interest;
                      const totalHeight = maxTotal > 0 ? (monthTotal / maxTotal) * maxBarHeight : 0;
                      const savingsHeight = maxTotal > 0 ? (savings / maxTotal) * maxBarHeight : 0;
                      const interestHeight = maxTotal > 0 ? (interest / maxTotal) * maxBarHeight : 0;
                      const isHovered = hoveredBar === `savings-${month}`;

                      return (
                        <div key={month} style={{ display: 'flex', alignItems: 'flex-end', minWidth: '30px', flexGrow: 1, justifyContent: 'center' }}>
                          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div
                              style={{
                                position: 'absolute',
                                top: '-36px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                display: isHovered ? 'block' : 'none',
                                zIndex: '1000',
                                pointerEvents: 'none'
                              }}
                            >
                              저축 {formatCurrency(savings)} / 이자 {formatCurrency(interest)}
                            </div>
                            <div
                              style={{
                                width: '24px',
                                height: `${totalHeight}px`,
                                display: 'flex',
                                flexDirection: 'column-reverse',
                                alignItems: 'center',
                                transition: 'height 0.3s ease',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={() => setHoveredBar(`savings-${month}`)}
                              onMouseLeave={() => setHoveredBar(null)}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: `${savingsHeight}px`,
                                  backgroundColor: SAVINGS_COLOR,
                                  minHeight: savingsHeight > 0 ? '2px' : 0,
                                  transition: 'height 0.3s ease'
                                }}
                              />
                              <div
                                style={{
                                  width: '100%',
                                  height: `${interestHeight}px`,
                                  backgroundColor: INTEREST_COLOR,
                                  minHeight: interestHeight > 0 ? '2px' : 0,
                                  transition: 'height 0.3s ease'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: '2%', justifyContent: 'center', paddingTop: '5px', paddingLeft: '0.5%', paddingRight: '0.5%' }}>
                    {sortedMonths.map(month => (
                      <div key={month} style={{ flexGrow: 1, minWidth: '30px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                        {month.split('-')[1]}월
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: SAVINGS_COLOR, borderRadius: '2px' }}></div>
                  <span style={{ fontSize: '14px' }}>저축</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: INTEREST_COLOR, borderRadius: '2px' }}></div>
                  <span style={{ fontSize: '14px' }}>이자</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="chart-placeholder">
              <p>저축/이자 데이터가 없습니다.</p>
              <p>계좌를 추가하고 거래를 등록해보세요.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSalaryTab = () => {
    // Group salaries by month, person, and classification (salary vs bonus)
    const monthlySalaries = {};
    salaries.forEach(salary => {
      const month = salary.month;
      const person = salary.person;
      const amount = salary.amount;
      const classification = salary.classification || '월급'; // Default to '월급' if not specified

      if (!monthlySalaries[month]) {
        monthlySalaries[month] = {
          민규: { salary: 0, bonus: 0, total: 0 },
          하영: { salary: 0, bonus: 0, total: 0 },
          total: 0
        };
      }

      if (classification === '보너스') {
        monthlySalaries[month][person].bonus += amount;
      } else {
        monthlySalaries[month][person].salary += amount;
      }
      monthlySalaries[month][person].total += amount;
      monthlySalaries[month].total += amount;
    });

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlySalaries).sort();

    // Calculate summary statistics
    const totalSalary = Object.values(monthlySalaries).reduce((sum, monthData) => sum + monthData.total, 0);
    const minGuiTotal = Object.values(monthlySalaries).reduce((sum, monthData) => sum + monthData.민규.total, 0);
    const haYoungTotal = Object.values(monthlySalaries).reduce((sum, monthData) => sum + monthData.하영.total, 0);

    // Calculate payment counts for each person
    const minGuiPaymentCount = salaries.filter(salary => salary.person === '민규').length;
    const haYoungPaymentCount = salaries.filter(salary => salary.person === '하영').length;

    // Calculate averages
    const averageSalary = salaries.length > 0 ? Math.round(totalSalary / salaries.length) : 0;
    const minGuiAverage = minGuiPaymentCount > 0 ? Math.round(minGuiTotal / minGuiPaymentCount) : 0;
    const haYoungAverage = haYoungPaymentCount > 0 ? Math.round(haYoungTotal / haYoungPaymentCount) : 0;

    // 순저축 = 저축+이자-지출 (현금 계좌 기준, monthlyIncome이 이미 이 규칙으로 집계됨).
    // 소비(비상금 등) 계좌는 이미 저축된 돈을 나중에 쓰는 것뿐이라 여기서는 완전히 제외된다.
    const netSavingsByMonth = {};
    sortedMonths.forEach((month) => {
      const d = monthlyIncome[month] || {};
      netSavingsByMonth[month] = (d.savings || 0) + (d.interest || 0) - (d.expense || 0);
    });
    const totalNetSavings = sortedMonths.reduce((sum, month) => sum + netSavingsByMonth[month], 0);

    // 계좌 내역에 거래로 남기지 않는 저축을 합치면 얼마가 되는지. 보여주기만 하고
    // totalNetSavings 에는 섞지 않는다 — 그쪽은 통장에 실제로 찍힌 숫자여야 한다.
    const offBookSavings = OFF_BOOK_SAVINGS[selectedYear] || null;
    const withOffBookSavings = offBookSavings ? totalNetSavings + offBookSavings.amount : null;

    // Find the maximum salary value for chart scaling (순저축 선도 같은 축을 쓰므로 함께 고려)
    const maxSalary = Math.max(
      ...Object.values(monthlySalaries).map(data => Math.max(data.민규.total, data.하영.total)),
      ...Object.values(netSavingsByMonth).map(v => Math.abs(v)),
      1
    );
    const maxBarHeight = 250; // Maximum bar height in pixels - 막대와 순저축 선이 공유하는 스케일

    // 순저축 선의 각 점 좌표: x는 실제 막대그룹의 DOM 중심을 측정한 값, y는 막대와 동일한 픽셀 공식
    const { points: salaryGeomPoints, areaHeight } = salaryLineGeometry;
    const salaryBaselineY = areaHeight - 20; // 막대의 paddingBottom(20px)과 같은 기준선
    const netSavingsLinePoints = salaryGeomPoints
      .filter(({ month }) => netSavingsByMonth[month] !== undefined)
      .map(({ month, x }) => {
        const value = netSavingsByMonth[month];
        const heightPx = maxSalary > 0 ? (value / maxSalary) * maxBarHeight : 0;
        return { month, x, y: salaryBaselineY - heightPx, value };
      });

    // Calculate y-axis grid values based on the new formula
    // Round up maxSalary to nearest 100,000 and divide by 5
    const roundedMaxSalary = Math.ceil(maxSalary / 100000) * 100000;
    const gridInterval = roundedMaxSalary / 5;
    const yAxisValues = [
      roundedMaxSalary,
      roundedMaxSalary - gridInterval,
      roundedMaxSalary - 2 * gridInterval,
      roundedMaxSalary - 3 * gridInterval,
      roundedMaxSalary - 4 * gridInterval,
      0
    ];

    return (
      <div className="tab-content">
        <div className="summary">
          <div className="summary-text">
            <p style={{ fontSize: '1.7rem', fontWeight: 'bold', marginBottom: '10px' }}>{selectedYear}년 월급 요약</p>
            {activeTab === 'salary' && (
              <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '1rem' }}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}년</option>
                  ))}
                </select>
              </div>
            )}
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '-5px', marginBottom: '5px' }}>** 월급의 기준은 전달 21일/25일에 들어온 금액이다. 예를 들어, 1월의 월급은 12월 21일/25일에 지급되었다.</p>
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '-5px', marginBottom: '15px' }}>** PS는 2월, PI는 7월, 8월, 12월에 합쳐 기록한다.</p>
            <p style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '8px' }}>
              총 월급: {formatCurrency(totalSalary)}
              {minGuiTotal > haYoungTotal ? (
                <> 👑(민규) {formatCurrency(minGuiTotal)} / (하영) {formatCurrency(haYoungTotal)}</>
              ) : haYoungTotal > minGuiTotal ? (
                <> (민규) {formatCurrency(minGuiTotal)} / 👑(하영) {formatCurrency(haYoungTotal)}</>
              ) : (
                <> (민규) {formatCurrency(minGuiTotal)} / (하영) {formatCurrency(haYoungTotal)}</>
              )}
            </p>
            <p style={{ fontSize: '0.95rem' }}>평균: {formatCurrency(averageSalary)} (민규) {formatCurrency(minGuiAverage)} / (하영) {formatCurrency(haYoungAverage)}</p>
            <p style={{ fontSize: '0.95rem' }}>중앙값: {formatCurrency(minGuiMedianSalary)} (민규) / {formatCurrency(haYoungMedianSalary)} (하영)</p>
            <p style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#d4ac00' }}>
              순저축액: {formatCurrency(totalNetSavings)}
              {totalSalary > 0 && ` (저축률 ${((totalNetSavings / totalSalary) * 100).toFixed(1)}%)`}
              {offBookSavings && (
                <span style={{ fontSize: '0.85rem', fontWeight: 'normal', color: '#888', marginLeft: '8px' }}>
                  {offBookSavings.label} {formatCurrency(withOffBookSavings)}
                  {totalSalary > 0 && ` (저축률 ${((withOffBookSavings / totalSalary) * 100).toFixed(1)}%)`}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="chart-container">
          {salaries.length > 0 ? (
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Chart title */}
              <div style={{ textAlign: 'center', marginBottom: '10px', fontSize: '18px', fontWeight: 'bold' }}>
                월급
              </div>

              {/* Chart area */}
              <div style={{ display: 'flex', height: '300px', marginBottom: '10px' }}>
                {/* Y-axis labels */}
                <div style={{ width: '55px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: '10px', fontSize: '10px', color: '#666', lineHeight: '1', position: 'relative', zIndex: '1', boxSizing: 'border-box', height: 'calc(100% - 20px)' }}>
                  <span>{(yAxisValues[0] / 10000).toLocaleString()}만원</span>
                  <span>{(yAxisValues[1] / 10000).toLocaleString()}만원</span>
                  <span>{(yAxisValues[2] / 10000).toLocaleString()}만원</span>
                  <span>{(yAxisValues[3] / 10000).toLocaleString()}만원</span>
                  <span>{(yAxisValues[4] / 10000).toLocaleString()}만원</span>
                  <span>0-</span>
                </div>

                {/* Chart area with grid lines */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                  {/* Bars */}
                  <div
                    ref={salaryChartAreaRef}
                    style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '2%', paddingBottom: '20px', borderBottom: '1px solid #ddd', position: 'relative', paddingLeft: '0.5%', paddingRight: '0.5%' }}
                    onMouseMove={(e) => {
                      console.log('Mouse moved in chart area - coordinates:', e.clientX, e.clientY);
                    }}
                  >
                    {/* Y-axis grid lines */}
                    <div style={{ position: 'absolute', top: '0px', left: 0, right: 0, bottom: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingTop: '0px', paddingBottom: '0px' }}>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} style={{ height: '1px', backgroundColor: '#eee', width: '100%', zIndex: '0' }}></div>
                      ))}
                    </div>
                    {sortedMonths.map((month, index) => {
                      const data = monthlySalaries[month];
                      // Use fixed height calculation instead of percentage
                      const maxBarHeight = 250; // Maximum bar height in pixels
                      const minGuiTotalHeight = maxSalary > 0 ? (data.민규.total / maxSalary) * maxBarHeight : 0;
                      const haYoungTotalHeight = maxSalary > 0 ? (data.하영.total / maxSalary) * maxBarHeight : 0;

                      // Calculate individual heights for salary and bonus
                      const minGuiSalaryHeight = maxSalary > 0 ? (data.민규.salary / maxSalary) * maxBarHeight : 0;
                      const minGuiBonusHeight = maxSalary > 0 ? (data.민규.bonus / maxSalary) * maxBarHeight : 0;
                      const haYoungSalaryHeight = maxSalary > 0 ? (data.하영.salary / maxSalary) * maxBarHeight : 0;
                      const haYoungBonusHeight = maxSalary > 0 ? (data.하영.bonus / maxSalary) * maxBarHeight : 0;

                      return (
                        <div
                          key={month}
                          ref={(el) => { salaryBarRefs.current[month] = el; }}
                          style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', minWidth: '30px', flexGrow: 1, justifyContent: 'center' }}
                        >
                          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div
                              style={{
                                position: 'absolute',
                                top: '-30px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                display: hoveredBar === `${month}-minGui` ? 'block' : 'none',
                                zIndex: '1000',
                                pointerEvents: 'none'
                              }}
                            >
                              {formatCurrency(data.민규.total)} ({formatCurrency(data.민규.bonus)}/{formatCurrency(data.민규.salary)})
                              {data.민규.bonus > 0 && (
                                <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                                  {salaries.filter(s => s.month === month && s.person === '민규' && s.classification === '보너스').map((s, i) => (
                                    <div key={i}>{formatCurrency(s.amount)}{s.memo ? ` - ${s.memo}` : ''}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                            {/* MinGui stacked bar: salary (opaque) + bonus (transparent) */}
                            <div
                              style={{
                                width: '15px',
                                height: `${minGuiTotalHeight}px`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                transition: 'height 0.3s ease',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={() => {
                                console.log('Mouse entered minGui bar for month:', month);
                                setHoveredBar(`${month}-minGui`);
                              }}
                              onMouseLeave={() => {
                                console.log('Mouse left minGui bar for month:', month);
                                setHoveredBar(null);
                              }}
                              onMouseMove={(e) => {
                                console.log('Mouse moved over minGui bar for month:', month, '- coordinates:', e.clientX, e.clientY);
                              }}
                              onClick={async (e) => {
                                // Determine which segment was clicked based on position
                                const rect = e.currentTarget.getBoundingClientRect();
                                const clickY = e.clientY - rect.top;
                                const isBonusClicked = clickY < minGuiBonusHeight;

                                if (isBonusClicked) {
                                  // Bonuses can be multiple per month/person, so open the list modal instead of a single record
                                  openBonusList(month, '민규');
                                  return;
                                }

                                // Find the appropriate salary data based on which segment was clicked
                                // We need to find the correct index in the original salaries array
                                let salaryIndex = -1;
                                for (let i = 0; i < salaries.length; i++) {
                                  const s = salaries[i];
                                  if (s.month === month && s.person === '민규' && (s.classification === undefined || s.classification === '월급')) {
                                    salaryIndex = i;
                                    break;
                                  }
                                }

                                if (salaryIndex !== -1) {
                                  // Get the actual salary data from the filtered array
                                  const selectedSalary = salaries[salaryIndex];

                                  // Now we need to find the index of this salary in the full array
                                  // Fetch all salaries to get the full array
                                  try {
                                    const allSalariesResponse = await statsApi.getSalaries();
                                    const allSalaries = allSalariesResponse.data;

                                    // Find the index in the full array that matches our selected salary
                                    let fullArrayIndex = -1;
                                    for (let i = 0; i < allSalaries.length; i++) {
                                      const s = allSalaries[i];
                                      if (s.month === selectedSalary.month &&
                                        s.person === selectedSalary.person &&
                                        s.amount === selectedSalary.amount &&
                                        s.classification === selectedSalary.classification) {
                                        fullArrayIndex = i;
                                        break;
                                      }
                                    }

                                    if (fullArrayIndex !== -1) {
                                      console.log('Clicked on salary data at index:', fullArrayIndex, 'in full array for month:', month, 'person: 민규', 'classification:', isBonusClicked ? '보너스' : '월급');
                                      setEditingSalary({ ...selectedSalary, index: fullArrayIndex });
                                      setShowEditModal(true);
                                    } else {
                                      // If we still can't find the data, show an error
                                      console.error('Could not find salary data in full array for', month, '민규', isBonusClicked ? '보너스' : '월급');
                                    }
                                  } catch (error) {
                                    console.error('Error fetching all salaries:', error);
                                  }
                                } else {
                                  // If we still can't find the data, show an error
                                  console.error('Could not find salary data for', month, '민규', isBonusClicked ? '보너스' : '월급');
                                }
                              }}
                              onContextMenu={async (e) => {
                                // For mobile long-press, we'll use context menu as a workaround
                                e.preventDefault();
                                const hasBonus = salaries.some(s => s.month === month && s.person === '민규' && s.classification === '보너스');
                                if (hasBonus) {
                                  openBonusList(month, '민규');
                                  return;
                                }
                                // Fall back to regular salary
                                let salaryIndex = -1;
                                for (let i = 0; i < salaries.length; i++) {
                                  const s = salaries[i];
                                  if (s.month === month && s.person === '민규' && (s.classification === undefined || s.classification === '월급')) {
                                    salaryIndex = i;
                                    break;
                                  }
                                }
                                if (salaryIndex !== -1) {
                                  // Get the actual salary data from the filtered array
                                  const selectedSalary = salaries[salaryIndex];

                                  // Now we need to find the index of this salary in the full array
                                  // Fetch all salaries to get the full array
                                  try {
                                    const allSalariesResponse = await statsApi.getSalaries();
                                    const allSalaries = allSalariesResponse.data;

                                    // Find the index in the full array that matches our selected salary
                                    let fullArrayIndex = -1;
                                    for (let i = 0; i < allSalaries.length; i++) {
                                      const s = allSalaries[i];
                                      if (s.month === selectedSalary.month &&
                                        s.person === selectedSalary.person &&
                                        s.amount === selectedSalary.amount &&
                                        s.classification === selectedSalary.classification) {
                                        fullArrayIndex = i;
                                        break;
                                      }
                                    }

                                    if (fullArrayIndex !== -1) {
                                      console.log('Clicked on salary data at index:', fullArrayIndex, 'in full array for month:', month, 'person: 민규');
                                      setEditingSalary({ ...selectedSalary, index: fullArrayIndex });
                                      setShowEditModal(true);
                                    } else {
                                      // If we still can't find the data, show an error
                                      console.error('Could not find salary data in full array for', month, '민규');
                                    }
                                  } catch (error) {
                                    console.error('Error fetching all salaries:', error);
                                  }
                                } else {
                                  console.error('Could not find salary data for', month, '민규');
                                }
                              }}
                            >
                              {/* Bonus segment (transparent) */}
                              <div
                                style={{
                                  width: '100%',
                                  height: `${minGuiBonusHeight}px`,
                                  backgroundColor: 'rgba(33, 150, 243, 0.4)', // More transparent blue for bonus
                                  transition: 'height 0.3s ease'
                                }}
                              ></div>
                              {/* Salary segment (opaque) */}
                              <div
                                style={{
                                  width: '100%',
                                  height: `${minGuiSalaryHeight}px`,
                                  backgroundColor: '#2196F3', // Opaque blue for salary
                                  transition: 'height 0.3s ease'
                                }}
                              ></div>
                            </div>
                          </div>
                          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div
                              style={{
                                position: 'absolute',
                                top: '-30px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                display: hoveredBar === `${month}-haYoung` ? 'block' : 'none',
                                zIndex: '1000',
                                pointerEvents: 'none'
                              }}
                            >
                              {formatCurrency(data.하영.total)} ({formatCurrency(data.하영.bonus)}/{formatCurrency(data.하영.salary)})
                              {data.하영.bonus > 0 && (
                                <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                                  {salaries.filter(s => s.month === month && s.person === '하영' && s.classification === '보너스').map((s, i) => (
                                    <div key={i}>{formatCurrency(s.amount)}{s.memo ? ` - ${s.memo}` : ''}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                            {/* HaYoung stacked bar: salary (opaque) + bonus (transparent) */}
                            <div
                              style={{
                                width: '15px',
                                height: `${haYoungTotalHeight}px`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                transition: 'height 0.3s ease',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={() => {
                                console.log('Mouse entered haYoung bar for month:', month);
                                setHoveredBar(`${month}-haYoung`);
                              }}
                              onMouseLeave={() => {
                                console.log('Mouse left haYoung bar for month:', month);
                                setHoveredBar(null);
                              }}
                              onMouseMove={(e) => {
                                console.log('Mouse moved over haYoung bar for month:', month, '- coordinates:', e.clientX, e.clientY);
                              }}
                              onClick={async (e) => {
                                // Determine which segment was clicked based on position
                                const rect = e.currentTarget.getBoundingClientRect();
                                const clickY = e.clientY - rect.top;
                                const isBonusClicked = clickY < haYoungBonusHeight;

                                if (isBonusClicked) {
                                  // Bonuses can be multiple per month/person, so open the list modal instead of a single record
                                  openBonusList(month, '하영');
                                  return;
                                }

                                // Find the appropriate salary data based on which segment was clicked
                                // We need to find the correct index in the original salaries array
                                let salaryIndex = -1;
                                for (let i = 0; i < salaries.length; i++) {
                                  const s = salaries[i];
                                  if (s.month === month && s.person === '하영' && (s.classification === undefined || s.classification === '월급')) {
                                    salaryIndex = i;
                                    break;
                                  }
                                }

                                if (salaryIndex !== -1) {
                                  // Get the actual salary data from the filtered array
                                  const selectedSalary = salaries[salaryIndex];

                                  // Now we need to find the index of this salary in the full array
                                  // Fetch all salaries to get the full array
                                  try {
                                    const allSalariesResponse = await statsApi.getSalaries();
                                    const allSalaries = allSalariesResponse.data;

                                    // Find the index in the full array that matches our selected salary
                                    let fullArrayIndex = -1;
                                    for (let i = 0; i < allSalaries.length; i++) {
                                      const s = allSalaries[i];
                                      if (s.month === selectedSalary.month &&
                                        s.person === selectedSalary.person &&
                                        s.amount === selectedSalary.amount &&
                                        s.classification === selectedSalary.classification) {
                                        fullArrayIndex = i;
                                        break;
                                      }
                                    }

                                    if (fullArrayIndex !== -1) {
                                      console.log('Clicked on salary data at index:', fullArrayIndex, 'in full array for month:', month, 'person: 하영', 'classification:', isBonusClicked ? '보너스' : '월급');
                                      setEditingSalary({ ...selectedSalary, index: fullArrayIndex });
                                      setShowEditModal(true);
                                    } else {
                                      // If we still can't find the data, show an error
                                      console.error('Could not find salary data in full array for', month, '하영', isBonusClicked ? '보너스' : '월급');
                                    }
                                  } catch (error) {
                                    console.error('Error fetching all salaries:', error);
                                  }
                                } else {
                                  console.error('Could not find salary data for', month, '하영');
                                }
                              }}
                              onContextMenu={async (e) => {
                                // For mobile long-press, we'll use context menu as a workaround
                                e.preventDefault();
                                const hasBonus = salaries.some(s => s.month === month && s.person === '하영' && s.classification === '보너스');
                                if (hasBonus) {
                                  openBonusList(month, '하영');
                                  return;
                                }
                                // Fall back to regular salary
                                let salaryIndex = -1;
                                for (let i = 0; i < salaries.length; i++) {
                                  const s = salaries[i];
                                  if (s.month === month && s.person === '하영' && (s.classification === undefined || s.classification === '월급')) {
                                    salaryIndex = i;
                                    break;
                                  }
                                }
                                if (salaryIndex !== -1) {
                                  // Get the actual salary data from the filtered array
                                  const selectedSalary = salaries[salaryIndex];

                                  // Now we need to find the index of this salary in the full array
                                  // Fetch all salaries to get the full array
                                  try {
                                    const allSalariesResponse = await statsApi.getSalaries();
                                    const allSalaries = allSalariesResponse.data;

                                    // Find the index in the full array that matches our selected salary
                                    let fullArrayIndex = -1;
                                    for (let i = 0; i < allSalaries.length; i++) {
                                      const s = allSalaries[i];
                                      if (s.month === selectedSalary.month &&
                                        s.person === selectedSalary.person &&
                                        s.amount === selectedSalary.amount &&
                                        s.classification === selectedSalary.classification) {
                                        fullArrayIndex = i;
                                        break;
                                      }
                                    }

                                    if (fullArrayIndex !== -1) {
                                      console.log('Clicked on salary data at index:', fullArrayIndex, 'in full array for month:', month, 'person: 하영');
                                      setEditingSalary({ ...selectedSalary, index: fullArrayIndex });
                                      setShowEditModal(true);
                                    } else {
                                      // If we still can't find the data, show an error
                                      console.error('Could not find salary data in full array for', month, '하영');
                                    }
                                  } catch (error) {
                                    console.error('Error fetching all salaries:', error);
                                  }
                                } else {
                                  console.error('Could not find salary data for', month, '하영');
                                }
                              }}
                            >
                              {/* Bonus segment (transparent) */}
                              <div
                                style={{
                                  width: '100%',
                                  height: `${haYoungBonusHeight}px`,
                                  backgroundColor: 'rgba(244, 67, 54, 0.4)', // More transparent red for bonus
                                  transition: 'height 0.3s ease'
                                }}
                              ></div>
                              {/* Salary segment (opaque) */}
                              <div
                                style={{
                                  width: '100%',
                                  height: `${haYoungSalaryHeight}px`,
                                  backgroundColor: '#f44336', // Opaque red for salary
                                  transition: 'height 0.3s ease'
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* 순저축 꺾은선 - 저축+이자-지출 (현금 계좌 기준) */}
                    {netSavingsLinePoints.length > 0 && (
                      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                        <polyline
                          points={netSavingsLinePoints.map(p => `${p.x},${p.y}`).join(' ')}
                          fill="none"
                          stroke="#d4ac00"
                          strokeWidth="1.25"
                        />
                        {netSavingsLinePoints.map((p) => {
                          const isHovered = hoveredSavingsMonth === p.month;
                          return (
                            <g
                              key={p.month}
                              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                              onMouseEnter={() => setHoveredSavingsMonth(p.month)}
                              onMouseLeave={() => setHoveredSavingsMonth(null)}
                            >
                              <circle cx={p.x} cy={p.y} r="10" fill="transparent" />
                              <circle cx={p.x} cy={p.y} r={isHovered ? 6 : 4.5} fill="#d4ac00" stroke="#fff" strokeWidth="1.5" />
                            </g>
                          );
                        })}
                      </svg>
                    )}

                    {/* 순저축 점 위에 마우스를 올리면 실제 금액과 그 달 월급 대비 저축률 표시 */}
                    {netSavingsLinePoints.map((p) => {
                      if (hoveredSavingsMonth !== p.month) return null;
                      const monthTotalSalary = monthlySalaries[p.month]?.total || 0;
                      const savingsRate = monthTotalSalary > 0 ? (p.value / monthTotalSalary) * 100 : 0;
                      return (
                        <div
                          key={`tip-${p.month}`}
                          style={{
                            position: 'absolute',
                            left: p.x,
                            top: p.y - 46,
                            transform: 'translateX(-50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            color: 'white',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            pointerEvents: 'none'
                          }}
                        >
                          순저축 {formatCurrency(p.value)} (저축률 {savingsRate.toFixed(1)}%)
                        </div>
                      );
                    })}
                  </div>

                  {/* X-axis labels */}
                  <div style={{ display: 'flex', gap: '2%', justifyContent: 'center', paddingTop: '5px', paddingLeft: '0.5%', paddingRight: '0.5%' }}>
                    {sortedMonths.map(month => (
                      <div key={month} style={{ flexGrow: 1, minWidth: '30px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                        {month.split('-')[1]}월
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '15px', height: '15px', backgroundColor: '#2196F3' }}></div>
                  <span style={{ fontSize: '14px' }}>민규</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '15px', height: '15px', backgroundColor: '#f44336' }}></div>
                  <span style={{ fontSize: '14px' }}>하영</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '15px', height: '15px', borderRadius: '50%', backgroundColor: '#d4ac00' }}></div>
                  <span style={{ fontSize: '14px' }}>순저축</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="chart-placeholder">
              <p>월급 데이터가 없습니다.</p>
              <p>월급 데이터를 추가해보세요.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderYearEndTaxTab = () => {
    const data = yearEndTax || {
      entries: [],
      totals: {},
      by_person: { 민규: {}, 하영: {} },
      grand_total: 0,
    };
    const taxPeople = ['민규', '하영'];
    const cardCategories = ['신용카드', '직불카드', '현금영수증'];
    const deductionRateByCategory = {
      신용카드: 0.15,
      직불카드: 0.3,
      현금영수증: 0.3,
    };
    const incomeByPerson = data.gross_income_by_person || {};
    const taxProgressByPerson = taxPeople.map((person) => {
      const categoryTotals = data.by_person?.[person] || {};
      const income = incomeByPerson[person] || 0;
      const threshold = income * 0.25;
      const cardSpend = cardCategories.reduce((sum, category) => sum + Number(categoryTotals[category] || 0), 0);
      const progress = threshold > 0 ? Math.min((cardSpend / threshold) * 100, 100) : 0;
      const remaining = Math.max(threshold - cardSpend, 0);
      const excess = Math.max(cardSpend - threshold, 0);
      const estimatedDeduction = cardSpend > 0 && excess > 0
        ? cardCategories.reduce((sum, category) => {
          const amount = Number(categoryTotals[category] || 0);
          const share = amount / cardSpend;
          return sum + (excess * share * deductionRateByCategory[category]);
        }, 0)
        : 0;
      return { person, income, threshold, cardSpend, progress, remaining, excess, estimatedDeduction, categoryTotals };
    });
    const topCategories = YEAR_END_TAX_CATEGORIES
      .map((category) => ({ category, amount: Number(data.totals?.[category] || 0) }))
      .filter((row) => row.amount > 0);
    const extractMonthFromNote = (note) => {
      const match = String(note || '').match(/(\d{1,2})\s*월/);
      if (!match) return 0;
      const month = Number(match[1]);
      return month >= 1 && month <= 12 ? month : 0;
    };
    const sortedEntries = [...(data.entries || [])].sort((a, b) => {
      const monthA = extractMonthFromNote(a.note);
      const monthB = extractMonthFromNote(b.note);
      if (monthA !== monthB) return monthB - monthA;

      const createdA = new Date(a.created_at || 0).getTime();
      const createdB = new Date(b.created_at || 0).getTime();
      if (createdA !== createdB) return createdB - createdA;

      return Number(b.id || 0) - Number(a.id || 0);
    });

    return (
      <div className="tab-content">
        <div className="summary">
          <div className="summary-text">
            <p style={{ fontSize: '1.7rem', fontWeight: 'bold', marginBottom: '12px' }}>연말정산</p>
            <p style={{ fontSize: '0.95rem', color: '#555', marginBottom: 0 }}>
              세전 총급여의 25%를 기준으로 신용카드, 직불카드, 현금영수증 사용액이 공제 구간에 얼마나 가까워졌는지 확인합니다.
            </p>
            {yearEndTaxError && <p style={{ color: '#c62828', marginTop: '10px' }}>{yearEndTaxError}</p>}
          </div>
        </div>

        <div className="chart-container" style={{ display: 'block', minHeight: 'auto', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <p style={{ fontWeight: 700, margin: 0 }}>{selectedYear}년 연말정산 진행률</p>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            {taxPeople.map((person) => (
              <div key={person} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', background: '#fbfcfd' }}>
                <label style={{ display: 'block', color: '#555', fontSize: '0.9rem', marginBottom: '6px' }}>{person} 세전 총급여</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    min="0"
                    step="1000000"
                    value={yearEndTaxGrossIncomeForm[person]}
                    onChange={(e) => setYearEndTaxGrossIncomeForm((prev) => ({ ...prev, [person]: e.target.value }))}
                    placeholder="예: 50000000"
                    style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <button
                    type="button"
                    onClick={() => handleSaveYearEndTaxGrossIncome(person)}
                    style={{ padding: '8px 12px', border: 'none', borderRadius: '4px', background: '#111', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  >
                    저장
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginBottom: '16px' }}>
            {taxProgressByPerson.map((row) => {
              const markerLeft = row.threshold > 0 ? Math.min(Math.max(row.progress, 0), 100) : 0;
              const markerTransform = markerLeft >= 94 ? '-100%' : markerLeft <= 6 ? '0' : '-50%';
              return (
              <div key={row.person} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'baseline', marginBottom: '10px' }}>
                  <p style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem' }}>{row.person}</p>
                  <span style={{ color: row.remaining > 0 ? '#c62828' : '#2e7d32', fontWeight: 800 }}>
                    {row.remaining > 0 ? `${formatCurrency(row.remaining)} 남음` : `${formatCurrency(row.excess)} 초과`}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px', fontSize: '0.9rem' }}>
                  <div>
                    <div style={{ color: '#666' }}>연간 소득</div>
                    <strong>{formatCurrency(row.income)}</strong>
                  </div>
                  <div>
                    <div style={{ color: '#666' }}>공제 진입 기준</div>
                    <strong>{formatCurrency(row.threshold)}</strong>
                  </div>
                  <div>
                    <div style={{ color: '#666' }}>카드/현금영수증 사용</div>
                    <strong>{formatCurrency(row.cardSpend)}</strong>
                  </div>
                  <div>
                    <div style={{ color: '#666' }}>간이 공제대상 추정</div>
                    <strong>{formatCurrency(row.estimatedDeduction)}</strong>
                  </div>
                </div>
                <div style={{ position: 'relative', paddingTop: '22px', paddingBottom: '24px', marginBottom: '10px' }}>
                  <span style={{ position: 'absolute', top: 0, right: 0, color: '#555', fontSize: '0.82rem', fontWeight: 700 }}>
                    목표 공제 금액
                  </span>
                  <div style={{ height: '14px', background: '#edf2f7', borderRadius: '999px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${markerLeft}%`,
                        height: '100%',
                        background: row.progress >= 100 ? '#2e7d32' : '#1976d2',
                        borderRadius: '999px',
                      }}
                    />
                  </div>
                  {row.threshold > 0 ? (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${markerLeft}%`,
                        bottom: 0,
                        transform: `translateX(${markerTransform})`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: markerLeft <= 6 ? 'flex-start' : markerLeft >= 94 ? 'flex-end' : 'center',
                        color: row.progress >= 100 ? '#2e7d32' : '#1976d2',
                        fontSize: '0.85rem',
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ lineHeight: 1, color: '#111' }}>▲</span>
                      <span>{row.progress.toFixed(1)}%</span>
                    </div>
                  ) : (
                    <div style={{ color: '#777', fontSize: '0.85rem', fontWeight: 700, marginTop: '6px' }}>
                      소득 입력 필요
                    </div>
                  )}
                </div>
                {cardCategories.map((category) => {
                  const amount = Number(row.categoryTotals?.[category] || 0);
                  const width = row.cardSpend > 0 ? Math.max((amount / row.cardSpend) * 100, amount > 0 ? 3 : 0) : 0;
                  return (
                    <div key={category} style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', marginBottom: '4px' }}>
                        <span>{category}</span>
                        <strong>{formatCurrency(amount)}</strong>
                      </div>
                      <div style={{ height: '8px', background: '#f1f3f5', borderRadius: '999px', overflow: 'hidden' }}>
                        <div
                          style={{
                            width: `${width}%`,
                            height: '100%',
                            background: category === '신용카드' ? '#ff7043' : category === '직불카드' ? '#26a69a' : '#7e57c2',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              );
            })}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px', marginBottom: '16px' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px' }}>
              <p style={{ fontWeight: 700, marginTop: 0 }}>홈택스 현금영수증 엑셀 업로드</p>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <select
                  value={yearEndTaxUploadPerson}
                  onChange={(e) => setYearEndTaxUploadPerson(e.target.value)}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="민규">민규</option>
                  <option value="하영">하영</option>
                </select>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  multiple
                  onChange={(e) => setYearEndTaxFiles(Array.from(e.target.files || []))}
                  style={{ flex: 1 }}
                />
              </div>
              <button
                type="button"
                onClick={handleImportCashReceipts}
                disabled={yearEndTaxImporting}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  background: yearEndTaxImporting ? '#90caf9' : '#111',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: yearEndTaxImporting ? 'default' : 'pointer',
                }}
              >
                {yearEndTaxImporting ? '업로드 중...' : '현금영수증 합산 등록'}
              </button>
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px' }}>
              <p style={{ fontWeight: 700, marginTop: 0 }}>수동 입력</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <select
                  value={yearEndTaxForm.person}
                  onChange={(e) => setYearEndTaxForm((prev) => ({ ...prev, person: e.target.value }))}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="민규">민규</option>
                  <option value="하영">하영</option>
                </select>
                <select
                  value={yearEndTaxForm.category}
                  onChange={(e) => setYearEndTaxForm((prev) => ({ ...prev, category: e.target.value }))}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  {YEAR_END_TAX_CATEGORIES.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={yearEndTaxForm.amount}
                  onChange={(e) => setYearEndTaxForm((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="금액"
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <input
                  value={yearEndTaxForm.note}
                  onChange={(e) => setYearEndTaxForm((prev) => ({ ...prev, note: e.target.value }))}
                  placeholder="메모"
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              <button
                type="button"
                onClick={handleAddYearEndTaxEntry}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#007bff',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                항목 추가
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontWeight: 700, marginBottom: '8px' }}>항목별 합계</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px' }}>
              {topCategories.length ? topCategories.map((row) => (
                <div key={row.category} style={{ border: '1px solid #eee', borderRadius: '6px', padding: '10px' }}>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>{row.category}</div>
                  <div style={{ fontWeight: 700 }}>{formatCurrency(row.amount)}</div>
                </div>
              )) : (
                <p style={{ color: '#777' }}>등록된 연말정산 항목이 없습니다.</p>
              )}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <p style={{ fontWeight: 700, marginBottom: '8px' }}>등록 내역</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>사람</th>
                  <th style={{ padding: '8px' }}>항목</th>
                  <th style={{ padding: '8px' }}>금액</th>
                  <th style={{ padding: '8px' }}>출처</th>
                  <th style={{ padding: '8px' }}>메모</th>
                  <th style={{ padding: '8px' }} />
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{entry.person}</td>
                    <td style={{ padding: '8px' }}>{entry.category}</td>
                    <td style={{ padding: '8px', fontWeight: 700 }}>{formatCurrency(Number(entry.amount || 0))}</td>
                    <td style={{ padding: '8px' }}>{entry.source === 'hometax_excel' ? '홈택스 엑셀' : '수동'}</td>
                    <td style={{ padding: '8px', color: '#666' }}>{entry.note || '-'}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => handleDeleteYearEndTaxEntry(entry.id)}
                        style={{ border: 'none', background: '#dc3545', color: '#fff', borderRadius: '4px', padding: '6px 8px', cursor: 'pointer' }}
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'portfolio':
        return renderPortfolioTab();
      case 'growth':
        return renderGrowthTab();
      case 'stock':
        return renderStockPortfolioTab();
      case 'salary':
        return renderSalaryTab();
      case 'yearEndTax':
        return renderYearEndTaxTab();
      case 'travel':
        return <TravelCosts />;
      default:
        return renderPortfolioTab();
    }
  };

  // Function to handle salary update
  const handleUpdateSalary = async (updatedSalary) => {
    try {
      await statsApi.updateSalary(updatedSalary.index, updatedSalary.amount, updatedSalary.month, updatedSalary.person, updatedSalary.classification, updatedSalary.memo);
      // Refresh the data
      fetchStats();
      // Close the modal
      setShowEditModal(false);
      setEditingSalary(null);
    } catch (error) {
      console.error('Error updating salary:', error);
      alert('Failed to update salary. Please try again.');
    }
  };

  // Fetch the full (unfiltered) bonus list for a month/person; indices returned here match
  // the backend's canonical ordering used by update/deleteSalary(index), unlike the
  // year-filtered `salaries` state.
  const loadBonusEntries = async (month, person) => {
    try {
      const allSalariesResponse = await statsApi.getSalaries();
      const allSalaries = allSalariesResponse.data;
      const entries = allSalaries
        .map((s, index) => ({ ...s, index }))
        .filter((s) => s.month === month && s.person === person && s.classification === '보너스');
      setBonusListEntries(entries);
    } catch (error) {
      console.error('Error fetching bonus entries:', error);
    }
  };

  const openBonusList = async (month, person) => {
    setBonusListContext({ month, person });
    setShowBonusListModal(true);
    await loadBonusEntries(month, person);
  };

  const handleBonusListChanged = async () => {
    if (bonusListContext) {
      await loadBonusEntries(bonusListContext.month, bonusListContext.person);
    }
    fetchStats();
  };

  // Function to handle salary deletion
  const handleDeleteSalary = async () => {
    if (!editingSalary) return;

    try {
      await statsApi.deleteSalary(editingSalary.index);
      // Refresh the data
      fetchStats();
      // Close the modal
      setShowEditModal(false);
      setEditingSalary(null);
    } catch (error) {
      console.error('Error deleting salary:', error);
      alert('Failed to delete salary. Please try again.');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => (currentYear - 5 + i).toString());

  return (
    <StatsContainer>
      <div className="stats-header">
        <h2>월별 통계</h2>
      </div>

      <div className="tabs">
        <div
          className={`tab ${activeTab === 'portfolio' ? 'active' : ''}`}
          onClick={() => setActiveTab('portfolio')}
        >
          🥧포트폴리오
        </div>
        <div
          className={`tab ${activeTab === 'growth' ? 'active' : ''}`}
          onClick={() => setActiveTab('growth')}
        >
          🌱자라나라 자산자산
        </div>
        <div
          className={`tab ${activeTab === 'salary' ? 'active' : ''}`}
          onClick={() => setActiveTab('salary')}
        >
          💵월급
        </div>
        <div
          className={`tab ${activeTab === 'yearEndTax' ? 'active' : ''}`}
          onClick={() => setActiveTab('yearEndTax')}
        >
          🧾연말정산
        </div>
        <div
          className={`tab ${activeTab === 'stock' ? 'active' : ''}`}
          onClick={() => setActiveTab('stock')}
        >
          📉주식 포폴
        </div>
        <div
          className={`tab ${activeTab === 'travel' ? 'active' : ''}`}
          onClick={() => setActiveTab('travel')}
        >
          ✈️여행 비용
        </div>
      </div>

      {renderTabContent()}

      {editingStockNote && (
        <div
          onClick={() => setEditingStockNote(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '420px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>{editingStockNote.person} 포폴 메모</h3>
            <textarea
              value={editingStockNote.note}
              onChange={(e) => setEditingStockNote({ ...editingStockNote, note: e.target.value })}
              placeholder="메모를 입력하세요."
              rows={6}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                resize: 'vertical',
                fontFamily: 'inherit',
                marginBottom: '15px',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setEditingStockNote(null)}
                style={{ backgroundColor: '#ccc', color: 'black', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer' }}
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSaveStockNote}
                style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer' }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {editingStockEntry && (
        <div
          onClick={() => setEditingStockEntry(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '400px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h3 style={{ marginTop: 0 }}>보유 종목 수정</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
              <div>
                <label>카테고리</label>
                <select
                  value={editingStockEntry.category}
                  onChange={(e) => setEditingStockEntry({
                    ...editingStockEntry,
                    category: e.target.value,
                    market: e.target.value === '현금' ? '원화' : '미국',
                    listing_market: e.target.value === '현금' ? '' : (editingStockEntry.listing_market || '국장'),
                  })}
                  style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                >
                  <option value="지수">지수</option>
                  <option value="섹터">섹터</option>
                  <option value="종목">종목</option>
                  <option value="현금">현금</option>
                </select>
              </div>
              <div>
                <label>{editingStockEntry.category === '현금' ? '통화' : '시장'}</label>
                <select
                  value={editingStockEntry.market}
                  onChange={(e) => setEditingStockEntry({ ...editingStockEntry, market: e.target.value })}
                  style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                >
                  {editingStockEntry.category === '현금' ? (
                    <>
                      <option value="원화">원화</option>
                      <option value="달러">달러</option>
                    </>
                  ) : (
                    <>
                      <option value="한국">국장</option>
                      <option value="미국">미장</option>
                      <option value="기타">기타</option>
                    </>
                  )}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>종목명</label>
              <input
                value={editingStockEntry.name}
                onChange={(e) => setEditingStockEntry({ ...editingStockEntry, name: e.target.value })}
                style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
              <div>
                <label>매입금액</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={editingStockEntry.purchase_amount}
                  onChange={(e) => setEditingStockEntry({ ...editingStockEntry, purchase_amount: e.target.value })}
                  style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label>현재 평가금액</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={editingStockEntry.current_amount}
                  onChange={(e) => setEditingStockEntry({ ...editingStockEntry, current_amount: e.target.value })}
                  style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                type="button"
                onClick={() => handleDeleteStockEntry(editingStockEntry.person, editingStockEntry.id)}
                style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer' }}
              >
                삭제
              </button>
              <div>
                <button
                  type="button"
                  onClick={() => setEditingStockEntry(null)}
                  style={{ backgroundColor: '#ccc', color: 'black', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleUpdateStockEntry}
                  style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer' }}
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Salary Modal */}
      {showEditModal && editingSalary && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0 }}>월급 수정</h3>
            <div style={{ marginBottom: '15px' }}>
              <label>날짜: </label>
              <input
                type="text"
                value={editingSalary.month}
                onChange={(e) => setEditingSalary({ ...editingSalary, month: e.target.value })}
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>이름: </label>
              <select
                value={editingSalary.person}
                onChange={(e) => setEditingSalary({ ...editingSalary, person: e.target.value })}
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              >
                <option value="민규">민규</option>
                <option value="하영">하영</option>
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>금액: </label>
              <input
                type="number"
                value={editingSalary.amount}
                onChange={(e) => setEditingSalary({ ...editingSalary, amount: parseFloat(e.target.value) || 0 })}
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>분류: </label>
              <select
                value={editingSalary.classification || '월급'}
                onChange={(e) => setEditingSalary({ ...editingSalary, classification: e.target.value })}
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              >
                <option value="월급">월급</option>
                <option value="보너스">보너스</option>
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>메모: </label>
              <input
                type="text"
                value={editingSalary.memo || ''}
                onChange={(e) => setEditingSalary({ ...editingSalary, memo: e.target.value })}
                placeholder="선택 사항"
                style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={handleDeleteSalary}
                style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer' }}
              >
                삭제
              </button>
              <div>
                <button
                  onClick={() => setShowEditModal(false)}
                  style={{ backgroundColor: '#ccc', color: 'black', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}
                >
                  취소
                </button>
                <button
                  onClick={() => handleUpdateSalary(editingSalary)}
                  style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer' }}
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BonusListModal
        isOpen={showBonusListModal}
        onClose={() => setShowBonusListModal(false)}
        month={bonusListContext?.month}
        person={bonusListContext?.person}
        entries={bonusListEntries}
        onChanged={handleBonusListChanged}
      />
    </StatsContainer>
  );
}

export default Stats;
